'use client';
import React, { useEffect, useRef, useState } from 'react';

interface WordDisplayProps {
  words: string[];
  currentWordIndex: number;
  wordStatuses: ('correct' | 'incorrect' | 'pending')[];
  currentInput: string;
  /** Whether the capture input is focused — drives the caret-blink state. */
  focused?: boolean;
  /** Whether to dim/blur the words as a "click to refocus" cue (mid-test only). */
  blurred?: boolean;
  /** Height of the scrollable words box in px (driven by the drag-resize grip). */
  height?: number;
}

type Caret = { left: number; top: number; height: number; ready: boolean };

export default function WordDisplay({ words, currentWordIndex, wordStatuses, currentInput, focused, blurred, height = 300 }: WordDisplayProps) {
  const currentRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const caretAnchorRef = useRef<HTMLSpanElement>(null);
  const [caret, setCaret] = useState<Caret>({ left: 0, top: 0, height: 0, ready: false });

  // After every keystroke / word change: keep the active line in view and move
  // the smooth caret to the next character's position. We measure an invisible
  // zero-width anchor rendered at the caret spot, then translate one absolutely
  // positioned caret element to it — CSS transitions the glide.
  useEffect(() => {
    const container = containerRef.current;
    const wordEl = currentRef.current;
    if (wordEl && container) {
      const lineHeight = wordEl.offsetHeight || 40;
      const wordTop = wordEl.offsetTop;
      const target = wordTop > lineHeight ? wordTop - lineHeight : 0;
      // Only animate when the line actually changes (avoids re-triggering a
      // smooth scroll on every keystroke within the same line).
      if (Math.abs(container.scrollTop - target) > 1) {
        const reduce = typeof window !== 'undefined' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        container.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    const anchor = caretAnchorRef.current;
    const content = contentRef.current;
    if (anchor && content) {
      // The anchor has an explicit 1.5rem-tall box (see .caret-anchor) that is
      // vertically centered on the line, so its rect maps straight to the caret.
      const a = anchor.getBoundingClientRect();
      const c = content.getBoundingClientRect();
      setCaret({
        left: a.left - c.left,
        top: a.top - c.top,
        height: a.height || 24,
        ready: true,
      });
    }
  }, [currentInput, currentWordIndex, words]);

  // Wheel scrolling can be swallowed when the app is embedded in an iframe
  // (the host frame may capture the wheel before it reaches this inner box).
  // Drive the scroll ourselves with a non-passive listener so it always works,
  // and only consume the event while the box can still scroll in that direction
  // — at the top/bottom edge we let it bubble so the host page keeps scrolling.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return; // pinch-zoom gesture — leave it alone
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight <= clientHeight) return; // nothing to scroll
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY; // lines → px
      const atTop = scrollTop <= 0 && delta < 0;
      const atBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight && delta > 0;
      if (atTop || atBottom) return; // let the host frame take over at the edges
      el.scrollTop += delta;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const renderCurrentWord = (word: string) => {
    const chars = word.split('');
    const typedLen = currentInput.length;
    const extra = typedLen > word.length ? currentInput.slice(word.length) : '';

    return (
      <>
        {chars.map((char, i) => {
          let cls = 'text-foreground';
          if (i < typedLen) {
            cls = currentInput[i] === char ? 'text-correct' : 'text-incorrect';
          }
          return (
            <React.Fragment key={`c-${i}`}>
              {i === typedLen && <span ref={caretAnchorRef} className="caret-anchor" aria-hidden="true" />}
              <span className={cls}>{char}</span>
            </React.Fragment>
          );
        })}
        {/* extra characters typed beyond the word length */}
        {extra && <span className="text-incorrect">{extra}</span>}
        {/* caret anchor at the very end when the word is fully (or over) typed */}
        {typedLen >= word.length && <span ref={caretAnchorRef} className="caret-anchor" aria-hidden="true" />}
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className="typing-area-wrapper typing-area-scroll font-mono-typing select-none"
      style={{
        height: `${height}px`,
        overflowY: 'auto',
        overflowX: 'hidden',
        lineHeight: '2.1',
        fontSize: '1.7rem',
      }}
      aria-label="Words to type"
      role="region"
    >
      <div
        ref={contentRef}
        className={`relative flex flex-wrap typing-words ${blurred ? 'is-blurred' : 'is-focused'}`}
        style={{ columnGap: '0.6rem', rowGap: '0.1rem' }}
      >
        {/* Single smooth caret — glides between positions via CSS transform */}
        {caret.ready && (
          <span
            className={`smooth-caret ${focused ? 'is-focused' : ''}`}
            style={{ transform: `translate(${caret.left}px, ${caret.top}px)`, height: `${caret.height}px` }}
            aria-hidden="true"
          />
        )}

        {words.map((word, wordIdx) => {
          const isCurrent = wordIdx === currentWordIndex;
          const isPast = wordIdx < currentWordIndex;

          const wordClass = isCurrent
            ? 'word-current'
            : isPast
            ? wordStatuses[wordIdx] === 'correct' ?'word-correct' :'word-incorrect' :'word-pending';

          return (
            <span
              key={`word-${wordIdx}-${word}`}
              ref={isCurrent ? currentRef : undefined}
              className={`relative inline-block whitespace-nowrap ${wordClass}`}
            >
              {isCurrent ? renderCurrentWord(word) : word}
            </span>
          );
        })}
      </div>
    </div>
  );
}
